<template>
  <aside
    class="sidebar"
    :class="{ 'is-open': isSidebarOpen, 'is-collapsed': isSidebarCollapsed }"
  >
    <nav tabindex="-1" id="sidebarMenu" aria-labelledby="sidebarMenuLabel">
      <div class="sidebar-header">
        <router-link to="/" class="sidebar-logo">
          <img
            src="@/assets/images/efashion-blanc.png"
            alt="Logo Efashion"
          />
        </router-link>
        <button
          @click="toggleSidebar"
          class="sidebar-close-btn"
          aria-label="Fermer le menu"
        >
          <span>&times;</span>
        </button>
      </div>

      <!-- Admin connecté (impersonation, cookie HttpOnly) -->
      <div
        v-if="
          authStore.isImpersonating &&
          authStore.impersonationDisplayName &&
          !isSidebarCollapsed
        "
        class="sidebar-admin-badge"
      >
        ADMIN : {{ authStore.impersonationDisplayName }}
      </div>

      <!-- Contenu normal de la sidebar -->
      <div class="sidebar-content" v-show="!isSidebarCollapsed">
        <div class="sidebar-nav">
          <ul>
            <li>
              <router-link
                aria-current="page"
                to="/dashboard"
                @click="closeSidebar"
              >
                <i class="fas fa-chart-pie"></i>
                Tableau de bord
              </router-link>
            </li>
            <!-- Mon compte -->
            <li>
              <span
                class="sidebar-collapse-trigger"
                @click="toggleCollapse('account')"
                :aria-expanded="isCollapseOpen.account"
                aria-controls="collapseAccountMenu"
              >
                <span><i class="fas fa-user-circle"></i>Mon compte</span>
                <i class="chevron fas fa-chevron-right"></i>
              </span>
              <div
                class="sidebar-collapse-content"
                id="collapseAccountMenu"
                :class="isCollapseOpen.account ? 'is-open' : 'is-closed'"
              >
                <ul>
                  <li>
                    <router-link :to="{ name: 'account/infos' }"
                      >Informations générales</router-link
                    >
                  </li>
                  <li>
                    <router-link :to="{ name: 'account/brands' }"
                      >Mes marques</router-link
                    >
                  </li>
                  <!-- Pays de livraison gratuite temporairement désactivé
                  <li><router-link :to="{ name: 'account/freedeliverycountries' }">Pays de livraison gratuite</router-link></li>
                  -->
                  <li>
                    <router-link :to="{ name: 'account/beuinfo' }"
                      >Informations BEU</router-link
                    >
                  </li>
                  <li>
                    <router-link :to="{ name: 'account/sales' }"
                      >Sectoriser mes ventes</router-link
                    >
                  </li>
                  <li>
                    <router-link :to="{ name: 'account/discount' }"
                      >Mes remises et promotion</router-link
                    >
                  </li>
                  <li>
                    <router-link :to="{ name: 'account/promocode' }"
                      >Gestions des codes promos</router-link
                    >
                  </li>

                  <!-- <li><router-link :to="{ name: 'account/promotionalemail' }">Email Promotionnel</router-link></li> -->
                  <!-- TEMP: Masquer le lien "Générer mon QR Code" -->
                  <!-- <li><router-link :to="{ name: 'account/qrcode' }">Générer mon qrcode</router-link></li> -->
                </ul>
              </div>
            </li>
            <!-- Catalogue -->
            <li>
              <span
                class="sidebar-collapse-trigger"
                @click="toggleCollapse('catalogue')"
                :aria-expanded="isCollapseOpen.catalogue"
                aria-controls="collapseCatalogueMenu"
              >
                <span><i class="fas fa-box"></i>Catalogue</span>
                <i class="chevron fas fa-chevron-right"></i>
              </span>
              <div
                class="sidebar-collapse-content sidebar-collapse-content--grouped"
                id="collapseCatalogueMenu"
                :class="isCollapseOpen.catalogue ? 'is-open' : 'is-closed'"
              >
                <div class="sidebar-menu-group">
                  <p class="sidebar-menu-group-title">Produits</p>
                  <ul>
                    <li>
                      <router-link :to="{ name: 'catalog/mel' }"
                        >Créer un produit</router-link
                      >
                    </li>
                    <li>
                      <router-link :to="{ name: 'catalog/productlist' }"
                        >Liste des produits</router-link
                      >
                    </li>
                    <li v-if="canAccessUploadPhotos">
                      <router-link :to="{ name: 'catalog/uploadphoto' }"
                        >Gestion des upload photos</router-link
                      >
                    </li>
                    <!-- <li><router-link :to="{ name: 'catalog/meaproduct' }">Gestion des mea</router-link></li> -->
                  </ul>
                </div>

                <div class="sidebar-menu-group">
                  <p class="sidebar-menu-group-title">Shooting</p>
                  <ul>
                    <li>
                      <router-link :to="{ name: 'catalog/shooting-request' }"
                        >Faire une demande</router-link
                      >
                    </li>
                    <li>
                      <router-link :to="{ name: 'catalog/listmel' }"
                        >Liste des mises en ligne</router-link
                      >
                    </li>
                  </ul>
                </div>

                <div class="sidebar-menu-group">
                  <p class="sidebar-menu-group-title">Tailles</p>
                  <ul>
                    <li>
                      <router-link :to="{ name: 'catalog/sizerange' }"
                        >Gestion des gammes de tailles</router-link
                      >
                    </li>
                    <li>
                      <router-link :to="{ name: 'catalog/packagemanagement' }"
                        >Gestion des paquets</router-link
                      >
                    </li>
                  </ul>
                </div>
              </div>
            </li>
            <!-- Commandes -->
            <li>
              <span
                class="sidebar-collapse-trigger"
                @click="toggleCollapse('order')"
                :aria-expanded="isCollapseOpen.order"
                aria-controls="collapseOrderMenu"
              >
                <span><i class="fas fa-clipboard-list"></i>Commandes</span>
                <i class="chevron fas fa-chevron-right"></i>
              </span>
              <div
                class="sidebar-collapse-content"
                id="collapseOrderMenu"
                :class="isCollapseOpen.order ? 'is-open' : 'is-closed'"
              >
                <ul>
                  <li>
                    <router-link
                      :to="{ name: 'myorders' }"
                      active-class=""
                      exact-active-class="router-link-exact-active"
                      >Mes commandes</router-link
                    >
                  </li>
                  <li>
                    <router-link
                      :to="{ name: 'facture' }"
                      active-class=""
                      exact-active-class="router-link-exact-active"
                      >Mes factures</router-link
                    >
                  </li>
                  <li>
                    <router-link
                      :to="{ name: 'my-litiges-retours' }"
                      active-class=""
                      exact-active-class="router-link-exact-active"
                      >Mes litiges et retours</router-link
                    >
                  </li>
                </ul>
              </div>
            </li>
            <!-- Modules -->
            <li>
              <span
                class="sidebar-collapse-trigger"
                @click="toggleCollapse('modules')"
                :aria-expanded="isCollapseOpen.modules"
                aria-controls="collapseModulesMenu"
              >
                <span><i class="fas fa-puzzle-piece"></i>Modules</span>
                <i class="chevron fas fa-chevron-right"></i>
              </span>
              <div
                class="sidebar-collapse-content"
                id="collapseModulesMenu"
                :class="isCollapseOpen.modules ? 'is-open' : 'is-closed'"
              >
                <ul>
                  <li>
                    <router-link :to="{ name: 'modules/lookbook' }"
                      >Gestion des lookbooks</router-link
                    >
                  </li>
                  <li>
                    <router-link :to="{ name: 'modules/buyers' }"
                      >Gestion des acheteurs</router-link
                    >
                  </li>
                </ul>
              </div>
            </li>
            <!-- Achat produits Efashion -->
            <li>
              <router-link
                :to="{ name: 'prestationproducts' }"
                @click="closeSidebar"
              >
                <i class="fas fa-store"></i>
                Store Efashion
              </router-link>
            </li>
            <!-- Services -->
            <li>
              <router-link
                :to="{ name: 'services' }"
                @click="closeSidebar"
              >
                <i class="fas fa-gem"></i>
                Services
              </router-link>
            </li>
          </ul>
        </div>
      </div>

      <!-- Icônes flottantes pour la sidebar rétractée -->
      <div class="sidebar-collapsed-icons" v-show="isSidebarCollapsed">
        <div
          v-if="authStore.isImpersonating && authStore.impersonationDisplayName"
          class="sidebar-admin-badge collapsed"
          :title="'ADMIN : ' + authStore.impersonationDisplayName"
        >
          ADMIN
        </div>
        <div
          class="collapsed-icon"
          @click="router.push('/')"
          title="Tableau de bord"
        >
          <i class="fas fa-home"></i>
        </div>
        <div
          class="collapsed-icon"
          @click="navigateTo('account/infos')"
          title="Informations générales"
        >
          <i class="fas fa-user-circle"></i>
        </div>
        <div
          class="collapsed-icon"
          @click="navigateTo('catalog/productlist')"
          title="Liste des produits"
        >
          <i class="fas fa-boxes"></i>
        </div>
        <div
          class="collapsed-icon"
          @click="navigateTo('myorders')"
          title="Mes commandes"
        >
          <i class="fas fa-clipboard-list"></i>
        </div>
      </div>
    </nav>
  </aside>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useAuthStore } from "@/stores/auth";

const authStore = useAuthStore();

interface Props {
  isSidebarOpen: boolean;
  isSidebarCollapsed: boolean;
  vendeurData: any;
}

interface Emits {
  (e: "toggle-sidebar"): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const router = useRouter();
const route = useRoute();

// Vérification du MEL pour afficher/cacher "Gestion des upload photos"
const canAccessUploadPhotos = computed(() => {
  // Priorité 1: mel depuis user du store
  let mel: string | number | null = null;
  if (authStore.user?.mel !== undefined) {
    mel = authStore.user.mel;
  } else {
    // Fallback: mel depuis localStorage
    const melFromStorage = authStore.getMelFromStorage();
    if (melFromStorage) {
      mel = melFromStorage;
    }
  }

  // Autoriser uniquement MEL = 1 (UPGR - Upload photos)
  // Bloquer MEL = 0 (eFashion) et MEL = 2 (TGR - Traitement grossiste)
  return mel === "1" || mel === 1;
});

const isCollapseOpen = ref({
  account: false,
  catalogue: false,
  order: false,
  modules: false,
});

const toggleSidebar = () => {
  emit("toggle-sidebar");
};

const closeSidebar = () => {
  emit("toggle-sidebar");
};

const toggleCollapse = (section: keyof typeof isCollapseOpen.value) => {
  if (isCollapseOpen.value[section]) {
    isCollapseOpen.value[section] = false;
  } else {
    Object.keys(isCollapseOpen.value).forEach((key) => {
      isCollapseOpen.value[key as keyof typeof isCollapseOpen.value] = false;
    });
    isCollapseOpen.value[section] = true;
  }
};

const navigateTo = (routeName: string) => {
  router.push({ name: routeName });
};

// Auto-expansion de la section correspondant à la route courante
const autoExpandSection = () => {
  const path = route.path;
  const name = route.name?.toString() || '';
  if (path.includes('/catalog') || name.startsWith('catalog/')) {
    isCollapseOpen.value.catalogue = true;
  } else if (path.includes('/account') || name.startsWith('account/')) {
    isCollapseOpen.value.account = true;
  } else if (path.includes('/order') || name.startsWith('myorders') || name === 'facture') {
    isCollapseOpen.value.order = true;
  } else if (path.includes('/modules') || name.startsWith('modules/')) {
    isCollapseOpen.value.modules = true;
  }
};

onMounted(() => {
  autoExpandSection();
});

watch(() => route.fullPath, () => {
  autoExpandSection();
});

// Auto-fermeture de la sidebar sur mobile après chaque navigation
const isMobile = () => window.matchMedia("(max-width: 768px)").matches;

watch(
  () => router.currentRoute.value.fullPath,
  () => {
    if (isMobile() && props.isSidebarOpen) {
      emit("toggle-sidebar");
    }
  },
);
</script>

<style lang="scss" scoped>
.sidebar-admin-badge {
  margin: 0.5rem 0.75rem;
  padding: 0.35rem 0.6rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #92400e;
  background-color: #fef3c7;
  border-radius: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &.collapsed {
    margin: 0.25rem auto;
    padding: 0.25rem 0.4rem;
    text-align: center;
    max-width: 4rem;
  }
}

/* Hide the left sidebar when printing the recap or any page */
@media print {
  .sidebar {
    display: none !important;
  }
}

.sidebar-badge-new {
  display: inline-block;
  margin-left: 0.5rem;
  padding: 0.1rem 0.4rem;
  font-size: 0.65rem;
  font-weight: 600;
  color: #dc2626;
  background-color: transparent;
  border-radius: 3px;
  vertical-align: middle;
}
</style>
